import { useLazyQuery, useQuery } from '@apollo/client';
import { CropOriginal } from '@mui/icons-material';
import { Box } from '@mui/material';
import type { GridRowSelectionModel } from '@mui/x-data-grid-pro';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useDeleteContent, useMoveContent } from '~/api/content';
import { FilterSidebar, FilterToggle } from '~/components/Filter';
import {
  ListActions,
  ListContainer,
  ListContent,
  ListHeader,
  ListPageContainer,
} from '~/components/PageLayout';
import { Count } from '~/components/count';
import { MoveToFolderDialog } from '~/components/dialogs/MoveToFolderDialog';
import { ConfirmDialog } from '~/components/dialogs/confirmation';
import { SearchBar } from '~/components/forms/search-bar';
import { Toolbar } from '~/components/toolbar';
import { useAppContext } from '~/contexts';
import type { ContentOrderBy } from '~/generated/graphql';
import { useConfirmDialog } from '~/hooks/dialogs';
import { useLink } from '~/hooks/link';
import { useOrderByParams } from '~/hooks/order-by';
import { usePaginationParams, useTotalCount } from '~/hooks/pagination';
import { assert } from '~/lib/assert';
import {
  BulkActions,
  ContentBulkTagDialog,
  ContentFilterAccordion,
  ContentPagination,
  ContentTable,
} from '../ContentFolderView/components';
import { useCanDestroySelected, useFilterParams, useFilters } from '../ContentFolderView/lib';
import { ContentMoveDocument } from '../components/ContentMove.generated';
import { ContentSearchDocument } from './ContentSearch.generated';

export const ContentSearch = () => {
  const { t } = useTranslation();
  const { currentNetwork } = useAppContext();
  const [searchParams, setSearchParams] = useSearchParams();
  const search = searchParams.get('search')?.trim() ?? '';
  const backToFolder = searchParams.get('backToFolder')?.trim() ?? '';
  const { kind, tags } = useFilterParams();
  const [selectedIds, setSelectedIds] = useState<GridRowSelectionModel>([]);
  const canDestroySelected = useCanDestroySelected(selectedIds);
  const [moveContentDialogOpen, setMoveContentDialogOpen] = useState(false);
  const [tagContentDialogOpen, setTagContentDialogOpen] = useState(false);
  const [moveContent, moveContentInfo] = useMoveContent();
  const [confirmDelete, confirmDeleteProps] = useConfirmDialog();

  const link = useLink();
  const navigate = useNavigate();

  const { handleOutOfBounds, page, perPage } = usePaginationParams();
  const { orderBy, setOrderByParams, sortModel } = useOrderByParams<ContentOrderBy>();
  const [searchContent, { data, loading }] = useLazyQuery(ContentSearchDocument);
  const [deleteContent] = useDeleteContent();

  const { data: contentMoveData } = useQuery(ContentMoveDocument, {
    variables: { networkId: currentNetwork.id },
    skip: !moveContentDialogOpen,
  });
  const editableFolderIds = useMemo(
    () =>
      contentMoveData?.network?.contentFolders.filter((x) => x.canUpdate.value).map((x) => x.id) ??
      [],
    [contentMoveData],
  );

  const totalCount = useTotalCount(data?.network?.content.pageInfo.nodesCount);

  useEffect(() => {
    if (!search) return;
    void searchContent({
      variables: {
        condition: { kind, search, tags },
        networkId: currentNetwork.id,
        ...(orderBy && { orderBy }),
        page,
        perPage,
      },
    }).then((result) => {
      if (result.data) {
        handleOutOfBounds(result.data.network?.content.pageInfo);
      }
    });
  }, [
    currentNetwork.id,
    handleOutOfBounds,
    kind,
    orderBy,
    page,
    perPage,
    search,
    searchContent,
    tags,
  ]);

  const { filter, setFilterItems, toggleAccordion, togglePanel } = useFilters(
    data?.network?.contentTags.map((x) => x.name),
  );

  const filtersActive = useMemo(() => kind.length + tags.length > 0, [kind, tags]);

  const handleClearFilter = useCallback(() => {
    backToFolder ? navigate(link(`/content/${backToFolder}`)) : navigate('..');
  }, [backToFolder, link, navigate]);

  const handleDeleteContent = useCallback(async () => {
    if (!(await confirmDelete())) return;
    if (selectedIds.length === 0) return;
    await deleteContent({
      variables: {
        items: selectedIds.map((x) => {
          assert(typeof x === 'string');
          const parts = x.split('-');
          return {
            contentableId: Number(parts[1]),
            contentableType: parts[0] === 'ContentItem' ? 'APP' : 'MEDIA',
          };
        }),
      },
    });
    setSelectedIds([]);
  }, [confirmDelete, deleteContent, selectedIds]);

  const handleSearch = useCallback(
    (newSearch: string) => {
      if (newSearch === '') {
        handleClearFilter();
        return;
      }
      setSearchParams((params) => {
        params.set('search', newSearch);
        params.set('backToFolder', backToFolder);
        return params;
      });
    },
    [backToFolder, handleClearFilter, setSearchParams],
  );

  return (
    <>
      <Helmet title={t('pages.content.search_title')} />
      <Toolbar titleIcon={<CropOriginal />} titleText={t('pages.content.search_title')} />

      <ListPageContainer>
        <FilterSidebar open={filter.panel.open} persistAs="contentSearch:filterWidth">
          <ContentFilterAccordion
            clearFilter={handleClearFilter}
            filters={filter.filters}
            filterCounts={data?.network?.contentFilterInfo}
            toggleAccordion={toggleAccordion}
            updateFilter={setFilterItems}
          />
        </FilterSidebar>
        <ListContainer>
          <ListHeader>
            <ListActions>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <FilterToggle active={filtersActive} onToggle={togglePanel} />
                <SearchBar
                  onChange={handleSearch}
                  placeholder={t('pages.content.search_placeholder')}
                  search={search}
                />
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                {selectedIds.length > 0 && (
                  <BulkActions
                    canDestroySelected={canDestroySelected}
                    onDeleteContent={handleDeleteContent}
                    onMoveContent={() => setMoveContentDialogOpen(true)}
                    onTagContent={() => setTagContentDialogOpen(true)}
                  />
                )}
                <Box sx={{ display: 'flex' }}>
                  <Count
                    selectedCount={selectedIds.length}
                    totalCount={totalCount}
                    thing="common.pluralize.item_plural"
                  />
                </Box>
              </Box>
            </ListActions>
          </ListHeader>
          <ListContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
              <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
                <ContentTable
                  items={data?.network?.content.nodes ?? []}
                  folders={[]}
                  loading={loading}
                  onCheck={setSelectedIds}
                  onSortModelChange={setOrderByParams}
                  search
                  selectedIds={selectedIds}
                  sortModel={sortModel}
                />
              </Box>
              <Box sx={{ flexShrink: 0 }}>
                <ContentPagination totalCount={totalCount} />
              </Box>
            </Box>
          </ListContent>
        </ListContainer>
      </ListPageContainer>

      <MoveToFolderDialog
        editableFolderIds={editableFolderIds}
        onSubmit={async (targetFolderId) => {
          await moveContent({
            refetchQueries: ['ContentFolderView', 'ContentSearch'],
            variables: {
              contentFolderId: targetFolderId,
              items: (selectedIds as string[]).map((x) => {
                const parts = x.split('-');
                return {
                  contentableId: Number(parts[1]),
                  contentableType: parts[0] === 'ContentItem' ? 'APP' : 'MEDIA',
                };
              }),
            },
          });
          setSelectedIds([]);
        }}
        loading={moveContentInfo.loading}
        onClose={() => setMoveContentDialogOpen(false)}
        open={moveContentDialogOpen}
        i18nKey="pages.content.dialogs.move_content.title"
      />

      <ConfirmDialog
        {...confirmDeleteProps}
        title={t('pages.content.dialogs.delete_content.title')}
        prompt={t('pages.content.dialogs.delete_content.prompt')}
        confirm={t('common.dialogs.permanently_delete')}
        deleteConfirm
      />

      {tagContentDialogOpen && (
        <ContentBulkTagDialog
          contentIds={selectedIds as string[]}
          contentTags={data?.network?.contentTags.map((x) => x.name) || []}
          onClose={() => setTagContentDialogOpen(false)}
          onComplete={() => setSelectedIds(() => [])}
          open
        />
      )}
    </>
  );
};
